import { getCustomerDetails, getMembershipTransactions, getSalesList } from "../APICalling/APICalling";

const OPEN_MEMBERSHIP_SALE_STATUSES = new Set([
	"partial",
	"pending",
	"pending payment",
	"in progress",
	"inprogress",
]);

const CLOSED_MEMBERSHIP_SALE_STATUSES = new Set([
	"paid",
	"closed",
	"completed",
	"complete",
	"cancelled",
	"canceled",
	"voided",
	"refunded",
]);

export const normalizeMembershipSaleStatus = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, " ");

export const getMembershipSaleId = (sale) => {
	if (sale?.is_payment_pending === true && (sale?.sale_id == null || String(sale.sale_id).trim() === "")) {
		return "";
	}
	const rawId = sale?.sale_id ?? sale?.booking_id ?? sale?.id ?? sale?.transaction_id;
	if (rawId == null || String(rawId).trim() === "") return "";
	return String(rawId).replace(/^#/, "").trim();
};

export const getOpenMembershipRecoveryIds = (sale) => ({
	transactionId: sale?.transaction_id ?? sale?.membership_transaction_id ?? null,
	membershipId:
		sale?.membership_id ??
		sale?.membership_detail?.id ??
		sale?.membershipDetail?.id ??
		null,
});

export const getOpenMembershipSaleAmount = (sale) => {
	const amount = Number(
		sale?.amount ??
		sale?.purchase_amount ??
		sale?.grand_total ??
		sale?.sub_total ??
		0
	);
	return Number.isFinite(amount) ? amount : 0;
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const isOpenPaymentStatus = (sale) => {
	// Authoritative flag when the row carries it (membership transactions API).
	if (sale?.is_payment_pending === true) return true;

	const status = normalizeMembershipSaleStatus(sale?.status);
	const paymentStatus = normalizeMembershipSaleStatus(sale?.payment_status);
	const compactStatus = status.replace(/\s+/g, "");
	const compactPaymentStatus = paymentStatus.replace(/\s+/g, "");

	if (
		CLOSED_MEMBERSHIP_SALE_STATUSES.has(status) ||
		CLOSED_MEMBERSHIP_SALE_STATUSES.has(paymentStatus)
	) {
		return false;
	}

	return (
		OPEN_MEMBERSHIP_SALE_STATUSES.has(status) ||
		OPEN_MEMBERSHIP_SALE_STATUSES.has(paymentStatus) ||
		OPEN_MEMBERSHIP_SALE_STATUSES.has(compactStatus) ||
		OPEN_MEMBERSHIP_SALE_STATUSES.has(compactPaymentStatus)
	);
};

export const isOpenMembershipSale = (sale) => {
	if (!sale || typeof sale !== "object") return false;

	const saleType = normalizeMembershipSaleStatus(sale?.type || sale?.sale_type || sale?.module);
	const action = normalizeMembershipSaleStatus(sale?.action || sale?.action_label);
	const looksLikeMembership =
		saleType === "membership" ||
		saleType === "membership sale" ||
		action.includes("membership") ||
		sale?.membership === 1 ||
		sale?.membership === true;

	if (!looksLikeMembership) return false;
	return isOpenPaymentStatus(sale);
};

// ---------------------------------------------------------------------------
// Response parsing
// ---------------------------------------------------------------------------

const extractSales = (response) => {
	const list = response?.data?.data?.list || response?.data?.list || response?.list || {};
	if (Array.isArray(list)) return list;
	if (Array.isArray(list.bookings)) return list.bookings;
	if (Array.isArray(list.sales)) return list.sales;
	if (Array.isArray(response?.data?.data)) return response.data.data;
	return [];
};

const getMembershipRecordId = (membershipDetail) => {
	const rawId = membershipDetail?.id ?? membershipDetail?.membership_id;
	if (rawId == null || String(rawId).trim() === "") return "";
	return String(rawId).replace(/^#/, "").trim();
};

const extractTransactionPayload = (response) => {
	const payloadData = response?.data?.data ?? response?.data ?? [];
	return Array.isArray(payloadData) ? payloadData[0] : payloadData;
};

const isPaymentPendingMembershipPayload = (payload) =>
	payload?.membership_detail?.payment_pending === true ||
	payload?.membershipDetail?.payment_pending === true;

const isMembershipDetailPaymentPending = (membershipDetail) =>
	membershipDetail?.payment_pending === true ||
	membershipDetail?.is_payment_pending === true ||
	Boolean(membershipDetail?.pending_topup_sale_id);

const hasTransactionPendingFlag = (transaction) =>
	Object.prototype.hasOwnProperty.call(transaction || {}, "is_payment_pending");

export const findPendingPaymentTransaction = (transactions) => {
	if (!Array.isArray(transactions)) return null;
	// A transaction is the open sale when the backend marks it pending OR its
	// payment_status is "partial" — both come straight from the transactions API.
	return (
		transactions.find(
			(item) =>
				item?.sale_id &&
				(item?.is_payment_pending === true ||
					normalizeMembershipSaleStatus(item?.payment_status) === "partial")
		) || null
	);
};

const mapPendingMembershipTransactionSale = (transaction, transactionPayload, membershipDetail) => ({
	...transaction,
	id: transaction?.sale_id,
	sale_id: transaction?.sale_id,
	transaction_id: transaction?.id ?? transaction?.transaction_id,
	// `type` routes this as a membership sale in the sales sidebar (refund/void);
	// the transaction payload carries no `type`, so it must be set explicitly.
	type: "membership",
	// Real status from the transaction ("partial"/…) instead of a hardcoded label.
	// The amount/paid_amount/balance_due also ride in via the transaction spread,
	// so the UI can show the true outstanding amount.
	status: transaction?.payment_status,
	membership_detail: transactionPayload?.membership_detail ?? membershipDetail,
});

// ---------------------------------------------------------------------------
// Pending membership transaction detection (1 API call)
// ---------------------------------------------------------------------------

/**
 * Check if a membership has a pending transaction.
 * Requires the membership record — does NOT fetch customer details.
 * API calls: 1 (getMembershipTransactions)
 */
export const findPendingMembershipTransactionSale = async (membershipDetail, outletId = null) => {
	const membershipRecordId = getMembershipRecordId(membershipDetail);
	if (!membershipRecordId) return null;

	const registrationOutletId =
		membershipDetail?.registration_outlet_id ??
		membershipDetail?.outlet_id ??
		outletId ??
		null;
	const transactionResponse = await getMembershipTransactions(membershipRecordId, {
		...(registrationOutletId ? { outlet: registrationOutletId } : {}),
	});
	const transactionPayload = extractTransactionPayload(transactionResponse);
	const transactions = Array.isArray(transactionPayload?.transactions)
		? transactionPayload.transactions
		: [];

	const pendingPaymentTransaction = findPendingPaymentTransaction(transactions);
	if (pendingPaymentTransaction) {
		return mapPendingMembershipTransactionSale(
			pendingPaymentTransaction,
			transactionPayload,
			membershipDetail
		);
	}

	if (transactions.some(hasTransactionPendingFlag)) return null;

	if (!isPaymentPendingMembershipPayload(transactionPayload)) return null;
	const transaction = transactions.find((item) => item?.sale_id) || transactions[0] || null;
	if (!transaction?.sale_id) return null;

	return mapPendingMembershipTransactionSale(transaction, transactionPayload, membershipDetail);
};

/**
 * Fetch customer details then check for pending membership transaction.
 * API calls: 1 (getCustomerDetails) + 1 (getMembershipTransactions) = 2
 *
 * Pass `membershipDetail` to skip the getCustomerDetails call (saves 1 API call).
 */
export const findPendingMembershipTransactionSaleForCustomer = async (customerId, outletId = null, membershipDetail = null) => {
	if (!customerId && !membershipDetail) return null;

	let detail = membershipDetail;
	if (!detail) {
		const customerResponse = await getCustomerDetails(customerId);
		const customerPayload = customerResponse?.data?.data ?? customerResponse?.data ?? customerResponse;
		detail = customerPayload?.membership_detail ?? customerPayload?.membershipDetail ?? null;
	}
	return findPendingMembershipTransactionSale(detail, outletId);
};

// ---------------------------------------------------------------------------
// Open membership sale detection via sales list (1–2 API calls)
// ---------------------------------------------------------------------------

/**
 * Search the sales list for an open membership sale.
 * API calls: 1 (best case) — 2 (worst case, user_id fallback)
 */
export const findOpenMembershipSaleForCustomer = async (customerId, outletId = null) => {
	if (!customerId) return null;

	const baseParams = {
		pagination: 1,
		per_page: 20,
		page: 1,
		customer: customerId,
	};
	if (outletId) baseParams.outlet = outletId;

	// Primary: outlet-scoped membership query
	const primaryResponse = await getSalesList({ ...baseParams, type: "membership" });
	const primarySales = extractSales(primaryResponse);
	const primaryMatch = primarySales.find((item) => isOpenPaymentStatus(item));
	if (primaryMatch) return primaryMatch;

	// If primary returned sales but none open → no pending membership sale exists
	if (primarySales.length > 0) return null;

	// Fallback: user_id-based lookup (some API endpoints use user_id instead of customer)
	const fallbackResponse = await getSalesList({
		pagination: 1, per_page: 20, page: 1,
		user_id: customerId,
		type: "membership",
		...(outletId ? { outlet: outletId } : {}),
	});
	return extractSales(fallbackResponse).find((item) => isOpenPaymentStatus(item)) || null;
};

// ---------------------------------------------------------------------------
// Combined search — sales list first, then pending transaction (cheaper path first)
// ---------------------------------------------------------------------------

/**
 * Find open membership sale OR pending transaction for a customer.
 * Used by PaymentPage / ConfirmationPage for the "Go to Open Sale" button.
 * API calls: 1–2 (sales list) + 0–2 (pending transaction fallback) = 1–4 total
 *
 * Pass `membershipDetail` to save 1 API call in the pending-transaction path.
 */
export const findOpenMembershipSaleOrPendingTransactionForCustomer = async (customerId, outletId = null, membershipDetail = null) => {
	if (isMembershipDetailPaymentPending(membershipDetail)) {
		const pendingSale = await findPendingMembershipTransactionSale(membershipDetail, outletId);
		if (pendingSale) return pendingSale;
	}

	// Try the cheaper sales-list search first (1–2 API calls)
	const saleSale = await findOpenMembershipSaleForCustomer(customerId, outletId);
	if (saleSale) return saleSale;

	// Fall back to pending membership transaction check (1–2 API calls)
	return findPendingMembershipTransactionSaleForCustomer(customerId, outletId, membershipDetail);
};

// ---------------------------------------------------------------------------
// Broadest search — ANY open sale (membership or booking)
// ---------------------------------------------------------------------------

/**
 * Find ANY open/partial sale for a customer — membership OR service booking.
 * Used by Client Details to show the "open sales" banner.
 * API calls: 1 (best case, open sale found) — 3 (worst case)
 *
 * Pass `membershipDetail` to skip getCustomerDetails in the pending-transaction path.
 */
export const findAnyOpenSaleForCustomer = async (customerId, outletId = null, membershipDetail = null) => {
	if (!customerId) return null;

	if (isMembershipDetailPaymentPending(membershipDetail)) {
		const pendingSale = await findPendingMembershipTransactionSale(membershipDetail, outletId);
		if (pendingSale) return pendingSale;
	}

	const baseParams = {
		pagination: 1,
		per_page: 20,
		page: 1,
		customer: customerId,
	};
	if (outletId) baseParams.outlet = outletId;

	// 1. Single broad query — finds ANY open sale (membership or booking) in one call
	const response = await getSalesList(baseParams);
	const allSales = extractSales(response);
	const openSale = allSales.find((item) => isOpenPaymentStatus(item));
	if (openSale) return openSale;

	// 2. If customer param returned nothing, try user_id param
	if (allSales.length === 0) {
		const fallbackResponse = await getSalesList({
			pagination: 1, per_page: 20, page: 1,
			user_id: customerId,
			...(outletId ? { outlet: outletId } : {}),
		});
		const fallbackMatch = extractSales(fallbackResponse).find((item) => isOpenPaymentStatus(item));
		if (fallbackMatch) return fallbackMatch;
	}

	// 3. Check pending membership transactions (suspended/inactive memberships
	//    that don't appear as sales yet). Pass membershipDetail to skip getCustomerDetails.
	return findPendingMembershipTransactionSaleForCustomer(customerId, outletId, membershipDetail);
};

export const buildOpenMembershipSaleUrl = (sale) => {
	const saleId = getMembershipSaleId(sale);
	if (!saleId) return "";
	const query = new URLSearchParams();
	query.set("search", saleId);
	query.set("step", "payment");
	return `/sales?${query.toString()}`;
};
